import React, { useState, useEffect, useCallback } from 'react';
import AppSpinner from "../Spinner/AppSpinner";
import CopyFooter from "../CopyFooter/CopyFooter";
import { Modal } from 'react-bootstrap';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import { useApiError } from '../../utils/useApiError';
import ValidationErrorModal from '../ValidationErrorModal/ValidationErrorModal';
import AddLectureModal from '../AddLectureModal/AddLectureModal';
import LectureDetail from '../LectureDetail/LectureDetail';

const thStyle = {
  padding: '8px 12px', textAlign: 'left',
  borderBottom: '1px solid #e5e7eb', color: '#374151',
  fontWeight: 700, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap'
};
const tdStyle = { padding: '6px 12px', color: '#374151', whiteSpace: 'nowrap' };
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5
};
const inputStyle = {
  width: '100%', border: '1px solid #e5e7eb', borderRadius: 6,
  padding: '7px 10px', fontSize: 12, color: '#111827', background: '#fff',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
};

const parseLang = (str, lang = 'Az') => {
  if (!str) return '';
  const regex = new RegExp(`${lang}:::([\\s\\S]*?)(?=\\s+[A-Z][a-z]+:::|$)`);
  const match = str.match(regex);
  if (match) return match[1].trim();
  return str;
};

/* Breadcrumb */
const Breadcrumb = ({ crumbs }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
    {crumbs.map((c, i) => (
      <React.Fragment key={i}>
        {i > 0 && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M4 2l4 4-4 4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        <span style={{
          fontSize: i === crumbs.length - 1 ? 13 : 11,
          fontWeight: i === crumbs.length - 1 ? 700 : 500,
          color: i === crumbs.length - 1 ? '#111827' : '#9ca3af',
          whiteSpace: 'nowrap'
        }}>{c}</span>
      </React.Fragment>
    ))}
  </div>
);

/* ModalHeader */
const ModalHeader = ({ crumbs, onClose, actions }) => (
  <Modal.Header style={{
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
    padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  }}>
    <Breadcrumb crumbs={crumbs} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {actions}
      <button type="button" className="btn-close" onClick={onClose} style={{ margin: 0 }} />
    </div>
  </Modal.Header>
);

/* Btn */
const Btn = ({ color, disabled, onClick, children }) => (
  <button onClick={onClick} disabled={disabled} style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: disabled
      ? (color === '#ef4444' ? '#fecaca' : color === '#3b82f6' ? '#bfdbfe' : '#d1d5db')
      : color,
    color: '#fff', border: 'none', borderRadius: 6,
    padding: '6px 12px', fontWeight: 600, fontSize: 12,
    cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'
  }}>{children}</button>
);

/* DeleteConfirmModal */
const DeleteConfirmModal = ({ show, onHide, onConfirm, name, crumbs }) => (
  <Modal show={show} onHide={onHide} centered>
    <ModalHeader crumbs={[...crumbs, 'Silməni Təsdiqlə']} onClose={onHide} />
    <Modal.Body style={{ padding: '20px', background: '#fff', fontSize: 13, color: '#374151' }}>
      <div style={{
        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
        padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start'
      }}>
        <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 18, color: '#ef4444', flexShrink: 0 }}></i>
        <span><strong>{parseLang(name)}</strong> adlı elementi silmək istədiyinizə əminsiniz? Bu əməliyyat geri alına bilməz.</span>
      </div>
    </Modal.Body>
    <Modal.Footer style={{
      background: '#f9fafb', borderTop: '1px solid #e5e7eb',
      padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8
    }}>
      <Btn color="#6b7280" onClick={onHide}>Ləğv et</Btn>
      <Btn color="#ef4444" onClick={onConfirm}>Bəli, Sil</Btn>
    </Modal.Footer>
  </Modal>
);

/* EditFormModal */
const EditFormModal = ({ show, onHide, crumbs, form, onChange, onSave, loading, error, labelName }) => (
  <Modal show={show} onHide={onHide} centered size="md">
    <ModalHeader crumbs={crumbs} onClose={onHide} />
    <Modal.Body style={{ padding: '20px 20px 8px', background: '#fff' }}>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{labelName} (AZ) <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          value={form.nameAz}
          onChange={e => onChange({ ...form, nameAz: e.target.value })}
      
          style={{ ...inputStyle, borderColor: error && !form.nameAz.trim() ? '#ef4444' : '#e5e7eb' }}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{labelName} (RU)</label>
        <input
          value={form.nameRu}
          onChange={e => onChange({ ...form, nameRu: e.target.value })}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{labelName} (EN)</label>
        <input
          value={form.nameEn}
          onChange={e => onChange({ ...form, nameEn: e.target.value })}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Təsvir</label>
        <textarea
          value={form.description}
          onChange={e => onChange({ ...form, description: e.target.value })}
          placeholder="Qısa məlumat"
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Quiz məcburidir?</label>
        <select
          value={String(form.mustHaveQuiz)}
          onChange={e => onChange({ ...form, mustHaveQuiz: e.target.value === 'true' })}
          style={inputStyle}
        >
          <option value="true">Bəli</option>
          <option value="false">Xeyr</option>
        </select>
      </div>
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, padding: '6px 12px', color: '#dc2626', fontSize: 12, marginBottom: 8
        }}>{error}</div>
      )}
    </Modal.Body>
    <Modal.Footer style={{
      background: '#f9fafb', borderTop: '1px solid #e5e7eb',
      padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8
    }}>
      <Btn color="#6b7280" onClick={onHide} disabled={loading}>Ləğv et</Btn>
      <Btn color="#3b82f6" onClick={onSave} disabled={loading}>
        {loading ? <><AppSpinner inline /> Saxlanılır...</> : 'Yadda saxla'}
      </Btn>
    </Modal.Footer>
  </Modal>
);

/* ═══════════ MOVE LECTURE MODAL ═══════════ */
const MoveLectureModal = ({ show, onHide, lecture, trainingId, currentSectionId, onSuccess }) => {
  const [sections, setSections]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [chosen, setChosen]         = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!show || !trainingId) return;
    setChosen(null);
    setError('');
    setLoading(true);
    apiFetch(`${BASE_URL}/sections/training/${trainingId}`)
      .then(r => r.json())
      .then(d => setSections((d.sections || []).filter(s => s.id !== currentSectionId)))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, [show, trainingId, currentSectionId]);

  const handleSave = async () => {
    if (!chosen) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(`${BASE_URL}/lectures/change-section`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lectureId: lecture.id, sectionId: chosen.id }),
      });
      if (!res.ok) throw new Error();
      onSuccess();
      onHide();
    } catch {
      setError('Köçürülən zaman xəta baş verdi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <i className="bi bi-arrow-left-right" style={{ color: '#2563eb', fontSize: 14 }}></i>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Dərsi köçür — <span style={{ color: '#2563eb' }}>{parseLang(lecture?.name)}</span>
          </span>
        </div>
        <button type="button" className="btn-close" onClick={onHide} style={{ margin: 0, flexShrink: 0 }} />
      </Modal.Header>
      <Modal.Body style={{ padding: '14px', background: '#fff', maxHeight: 340, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
            <AppSpinner text="Bölmələr yüklənir..." />
          </div>
        ) : sections.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '30px 0' }}>
            Başqa bölmə yoxdur
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sections.map(s => {
              const isSel = chosen?.id === s.id;
              return (
                <div
                  key={s.id}
                  onClick={() => setChosen(s)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${isSel ? '#2563eb' : '#e5e7eb'}`,
                    background: isSel ? 'rgba(37,99,235,0.07)' : '#fff',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? 'rgba(37,99,235,0.07)' : '#fff'; }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isSel ? '#2563eb' : '#d1d5db'}`,
                    background: isSel ? '#2563eb' : '#fff', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                  }}>
                    {isSel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: isSel ? 700 : 500, color: isSel ? '#1d4ed8' : '#374151' }}>
                    {parseLang(s.name)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {error && (
          <div style={{ marginTop: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 12px', color: '#dc2626', fontSize: 12 }}>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '10px 14px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Btn color="#6b7280" onClick={onHide} disabled={saving}>Ləğv et</Btn>
        <Btn color="#2563eb" onClick={handleSave} disabled={saving || !chosen}>
          {saving ? <><AppSpinner inline /> Köçürülür...</> : <><i className="bi bi-arrow-left-right"></i> Köçür</>}
        </Btn>
      </Modal.Footer>
    </Modal>
  );
};

/* ═══════════ MAIN ═══════════ */
const SectionLectures = ({ show, onHide, sectionId, sectionName, trainingName, trainingId }) => {
  const [lectures, setLectures]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selected, setSelected]         = useState(null);
  const [copiedText, setCopiedText]     = useState('');
  const [modal, setModal]               = useState('');
  const [editForm, setEditForm]         = useState({ nameAz: '', nameRu: '', nameEn: '', description: '', mustHaveQuiz: true });
  const [editLoading, setEditLoading]   = useState(false);
  const { errorModal, clearError, handleApiError } = useApiError();
  const [editError, setEditError]       = useState('');
  const [detailId, setDetailId]         = useState(null);
  const [isDirty, setIsDirty]           = useState(false);
  const [saveOrderLoading, setSaveOrderLoading] = useState(false);
  const dragIdx     = React.useRef(null);
  const dragOverIdx = React.useRef(null);

  const openModal  = (name) => setModal(name);
  const closeModal = ()     => setModal('');

  const fetchLectures = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(`${BASE_URL}/lectures/section/${sectionId}`),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      const data = await res.json();
      const sorted = (data.lectures || []).slice().sort((a, b) => (a.counter ?? 0) - (b.counter ?? 0));
      setLectures(sorted);
      setSelected(null);
      setIsDirty(false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sectionId]);

  const handleSaveOrder = async () => {
    setSaveOrderLoading(true);
    const payload = { lectures: lectures.map((l, i) => ({ lectureId: l.id, counter: i + 1 })) };
    try {
      const res = await apiFetch(`${BASE_URL}/lectures/update-counters`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); handleApiError(d); return; }
      setIsDirty(false);
    } catch (e) { handleApiError({ message: 'Sıra yadda saxlanılmadı.' }); }
    finally { setSaveOrderLoading(false); }
  };

  const handleDragStart = (i) => { dragIdx.current = i; };
  const handleDragOver  = (e, i) => { e.preventDefault(); dragOverIdx.current = i; };
  const handleDrop      = () => {
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const updated = [...lectures];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setLectures(updated);
    setIsDirty(true);
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  useEffect(() => {
    if (show && sectionId) fetchLectures();
  }, [show, sectionId, fetchLectures]);

  const handleOpenEdit = () => {
    if (!selected) return;
    setEditForm({
      nameAz: parseLang(selected.name, 'Az'),
      nameRu: parseLang(selected.name, 'Rus'),
      nameEn: parseLang(selected.name, 'Eng'),
      description: selected.description || '',
      mustHaveQuiz: selected.mustHaveQuiz !== false
    });
    setEditError('');
    openModal('edit');
  };

  const handleEdit = async () => {
    if (!editForm.nameAz.trim()) { setEditError('Dərs adı (AZ) mütləqdir.'); return; }
    setEditLoading(true);
    const combinedName = `Az::: ${editForm.nameAz.trim()} Rus::: ${editForm.nameRu.trim()} Eng::: ${editForm.nameEn.trim()}`;
    try {
      const res = await apiFetch(`${BASE_URL}/lectures`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selected.id,
          name: combinedName,
          description: editForm.description,
          mustHaveQuiz: editForm.mustHaveQuiz
        })
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); handleApiError(d); return; }
      closeModal(); fetchLectures();
    } catch { handleApiError({ message: 'Redaktə edilərkən xəta baş verdi.' }); }
    finally { setEditLoading(false); }
  };

  const handleDelete = async () => {
    try {
      const res = await apiFetch(`${BASE_URL}/lectures/${selected.id}`, {
        method: 'DELETE',
      });
      if (res.ok) { closeModal(); fetchLectures(); }
      else { const d = await res.json().catch(() => ({})); handleApiError(d); }
    } catch (e) { handleApiError({ message: 'Silinərkən xəta baş verdi.' }); }
  };

  const bc = trainingName
    ? [trainingName, 'Bölmələr', parseLang(sectionName) || 'Dərslər']
    : [parseLang(sectionName) || 'Dərslər'];

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" scrollable
        contentClassName="position-relative overflow-hidden">
        <ModalHeader
          crumbs={bc}
          onClose={onHide}
          actions={<>
            <Btn color="#2563eb" onClick={fetchLectures}>
              <svg viewBox="0 0 20 20" fill="none" style={{ width: 13, height: 13 }}>
                <path d="M4 4a7 7 0 0 1 12 4.9M4 10a7 7 0 0 0 12 4.9M16 4v4h-4M4 16v-4h4"
                  stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Btn>
            {isDirty && (
              <button onClick={handleSaveOrder} disabled={saveOrderLoading} title="Sıranı yadda saxla" style={{
                width: 28, height: 28, border: 'none', borderRadius: 6,
                background: '#2563eb', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: saveOrderLoading ? 'not-allowed' : 'pointer', flexShrink: 0
              }}>
                {saveOrderLoading ? <AppSpinner inline /> : (
                  <svg viewBox="0 0 20 20" fill="none" style={{ width: 13, height: 13 }}>
                    <rect x="3" y="3" width="14" height="14" rx="2" stroke="white" strokeWidth="1.6"/>
                    <rect x="6" y="3" width="8" height="5" rx="1" stroke="white" strokeWidth="1.4"/>
                    <rect x="6" y="11" width="8" height="5" rx="1" stroke="white" strokeWidth="1.4"/>
                    <line x1="8" y1="3" x2="8" y2="8" stroke="white" strokeWidth="1.4"/>
                  </svg>
                )}
              </button>
            )}
            <Btn color="#2563eb" disabled={!selected} onClick={() => openModal('move')}>
              <i className="bi bi-arrow-left-right"></i> Köçür
            </Btn>
            <Btn color="#3b82f6" disabled={!selected} onClick={handleOpenEdit}>Redaktə et</Btn>
            <Btn color="#ef4444" disabled={!selected} onClick={() => openModal('delete')}>Sil</Btn>
            <Btn color="#22c55e" onClick={() => openModal('add')}>+ Yeni Dərs</Btn>
          </>}
        />

        <Modal.Body style={{ padding: 0, background: '#fff', minHeight: 300 }}>
          <AppSpinner show={loading} text="Dərslər yüklənir..." minMs={200} />
          {!loading && (lectures.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 5 }}>
                    <th style={{ ...thStyle, width: 28, padding: '8px 6px' }}></th>
                    <th style={thStyle}>KOD</th>
                    <th style={thStyle}>DƏRS ADI</th>
                    <th style={thStyle}>TƏSVİR</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>TARİX</th>
                  </tr>
                </thead>
                <tbody>
                  {lectures.map((l, idx) => {
                    const isSel = selected?.id === l.id;
                    const displayName = parseLang(l.name);
                    const displayDesc = parseLang(l.description);
                    return (
                      <tr key={l.id}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={e => handleDragOver(e, idx)}
                        onDrop={handleDrop}
                        onDragEnd={() => { dragIdx.current = null; dragOverIdx.current = null; }}
                        onClick={(e) => setSelected(e.ctrlKey && isSel ? null : l)}
                        onDoubleClick={() => {
                          setSelected(l);
                          setDetailId(l.id);
                          if (sectionName) localStorage.setItem('lastSectionName', sectionName);
                          openModal('detail');
                        }}
                        style={{
                          borderBottom: '1px solid #e5e7eb', height: 35,
                          cursor: 'grab', background: isSel ? '#eff6ff' : '#fff',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isSel ? '#eff6ff' : '#fff'; }}
                      >
                        <td style={{ ...tdStyle, padding: '6px', color: '#d1d5db', cursor: 'grab', userSelect: 'none' }}>
                          <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: 14, height: 14, display: 'block', margin: '0 auto' }}>
                            <circle cx="5" cy="4" r="1.2"/><circle cx="11" cy="4" r="1.2"/>
                            <circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/>
                            <circle cx="5" cy="12" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
                          </svg>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px',
                            borderRadius: 4, fontWeight: 700, fontSize: 11, border: '1px solid #bfdbfe'
                          }}>#{l.id}</span>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }} onClick={() => setCopiedText(displayName)}>
                          {displayName}
                        </td>
                        <td style={{ ...tdStyle, color: '#6b7280', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={displayDesc} onClick={() => setCopiedText(displayDesc)}>
                          {displayDesc || '—'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280' }} onClick={() => setCopiedText(l.register ? l.register.split('T')[0] : '')}>
                          {l.register ? l.register.split('T')[0] : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: '#9ca3af' }}>
              <i className="bi bi-journal-richtext" style={{ fontSize: 36, marginBottom: 10, display: 'block', color: '#d1d5db' }}></i>
              <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 4 }}>Hələ ki dərs yoxdur</h6>
              <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Bu bölmə üçün dərs təyin edilməyib.</p>
            </div>
          ))}
        </Modal.Body>
        <CopyFooter value={copiedText} />
        {modal && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(0,0,0,0.5)',
            borderRadius: 'inherit'
          }} />
        )}
      </Modal>

      <EditFormModal show={modal === 'edit'} onHide={closeModal}
        crumbs={[...bc, 'Redaktə']}
        form={editForm} onChange={setEditForm}
        onSave={handleEdit} loading={editLoading} error={editError}
        labelName="Dərs Adı" />

      <DeleteConfirmModal show={modal === 'delete'} onHide={closeModal}
        onConfirm={handleDelete} name={selected?.name} crumbs={bc} />

      <AddLectureModal show={modal === 'add'} onHide={closeModal}
        sectionId={sectionId} sectionName={sectionName} onSuccess={fetchLectures} />

      <LectureDetail show={modal === 'detail'} onHide={closeModal} lectureId={detailId} />

      <MoveLectureModal
        show={modal === 'move'}
        onHide={closeModal}
        lecture={selected}
        trainingId={trainingId}
        currentSectionId={sectionId}
        onSuccess={() => { closeModal(); fetchLectures(); setSelected(null); }}
      />
      <ValidationErrorModal show={!!errorModal} onHide={clearError} message={errorModal?.message} validations={errorModal?.validations} />
    </>
  );
};

export default SectionLectures;